export interface StripJsonOptions {
    /**
     Strip trailing commas in addition to comments.

     @default false
     */
    trailingCommas?: boolean;

    /**
     Replace comments and trailing commas with whitespace instead of stripping them entirely.

     @default true
     */
    whitespace?: boolean;
}

export class StringUtils {

    public static uuid(): string {
        let d: string = "";
        while (d.length < 32) {
            d += Math.random().toString(16).substring(2);
        }
        const vr:string = ((parseInt(d.substring(16, 17), 16) & 0x3) | 0x8).toString(16);
        return `${d.substring(0, 8)}-${d.substring(8, 12)}-4${d.substring(13, 16)}-${vr}${d.substring(17, 20)}-${d.substring(20, 32)}`;
    }

    private static singleComment: Symbol = Symbol("singleComment");
    private static multiComment: Symbol = Symbol('multiComment');

    public static isEscaped(jsonString: string, quotePosition: number): boolean {
        let index: number = quotePosition - 1;
        let backslashCount: number = 0;

        while (jsonString[index] === '\\') {
            index -= 1;
            backslashCount += 1;
        }

        return !!(backslashCount % 2);
    };

    public static stripJsonComments(jsonString: string, {whitespace = true, trailingCommas = false}: StripJsonOptions = {}): string {
        if (typeof jsonString !== 'string') {
            throw new TypeError("Expected argument `jsonString` to be a `string`, got `${typeof jsonString}`");
        }

        const strip: (s: string, start?: number, end?: number) => string = whitespace
            ? (s, start, end) => s.slice(start, end).replace(/\S/g, " ")
            : () => "";

        let isInsideString: boolean = false;
        let isInsideComment: boolean | Symbol = false;
        let offset: number = 0;
        let buffer: string = "";
        let result: string = "";
        let commaIndex: number = -1;

        for (let index: number = 0; index < jsonString.length; index++) {
            const currentCharacter: string = jsonString[index];
            const nextCharacter: string = jsonString[index + 1];

            if (!isInsideComment && currentCharacter === "\"") {
                // Enter or exit string
                const escaped: boolean = StringUtils.isEscaped(jsonString, index);
                if (!escaped) {
                    isInsideString = !isInsideString;
                }
            }

            if (isInsideString) {
                continue;
            }

            if (!isInsideComment && currentCharacter + nextCharacter === "//") {
                // Enter single-line comment
                buffer += jsonString.slice(offset, index);
                offset = index;
                isInsideComment = StringUtils.singleComment;
                index++;
                continue;
            }

            if (isInsideComment === StringUtils.singleComment && currentCharacter + nextCharacter === "\r\n") {
                // Exit single-line comment via \r\n
                index++;
                isInsideComment = false;
                buffer += strip(jsonString, offset, index);
                offset = index;
                continue;
            }

            if (isInsideComment === StringUtils.singleComment && currentCharacter === "\n") {
                // Exit single-line comment via \n
                isInsideComment = false;
                buffer += strip(jsonString, offset, index);
                offset = index;
                continue;
            }

            if (!isInsideComment && currentCharacter + nextCharacter === "/*") {
                // Enter multiline comment
                buffer += jsonString.slice(offset, index);
                offset = index;
                isInsideComment = StringUtils.multiComment;
                index++;
                continue;
            }

            if (isInsideComment === StringUtils.multiComment && currentCharacter + nextCharacter === "*/") {
                // Exit multiline comment
                index++;
                isInsideComment = false;
                buffer += strip(jsonString, offset, index + 1);
                offset = index + 1;
                continue;
            }

            if (trailingCommas && !isInsideComment) {
                if (commaIndex !== -1) {
                    if (currentCharacter === "}" || currentCharacter === "]") {
                        // Strip trailing comma
                        buffer += jsonString.slice(offset, index);
                        result += strip(buffer, 0, 1) + buffer.slice(1);
                        buffer = "";
                        offset = index;
                        commaIndex = -1;
                    } else if (currentCharacter !== " " && currentCharacter !== "\t" && currentCharacter !== "\r" && currentCharacter !== "\n") {
                        // Hit non-whitespace following a comma; comma is not trailing
                        buffer += jsonString.slice(offset, index);
                        offset = index;
                        commaIndex = -1;
                    }
                } else if (currentCharacter === ",") {
                    // Flush buffer prior to this point, and save new comma index
                    result += buffer + jsonString.slice(offset, index);
                    buffer = "";
                    offset = index;
                    commaIndex = index;
                }
            }
        }

        return result + buffer + (isInsideComment ? strip(jsonString.slice(offset)) : jsonString.slice(offset));
    }
}
